import * as Tone from "tone";
import { MultiKeyInstrument } from "./sampleUtils";
import * as PitchFinder from "pitchfinder";

// Global audio processing chain
let globalFilter: Tone.Filter | null = null;
let globalReverb: Tone.Reverb | null = null;
let globalDelay: Tone.FeedbackDelay | null = null;
let globalEnvelope: Tone.AmplitudeEnvelope | null = null;

// ADSR envelope parameters
let globalAttack = 0.01; // Default attack time in seconds
let globalDecay = 0.2; // Default decay time in seconds
let globalSustain = 0.7; // Default sustain level (0-1)
let globalRelease = 0.5; // Default release time in seconds
let globalAdsrEnabled = true;

const ADSR_GATE_ATTACK = 0.001;
const ADSR_GATE_DECAY = 0.001;
const ADSR_GATE_SUSTAIN = 1;
const ADSR_GATE_RELEASE = 0.006;
const SAMPLER_DEFAULT_RELEASE = 0.1;
const SAMPLER_CUT_RELEASE = 0.006;

interface EnvelopeSettings {
  attack: number;
  decay: number;
  sustain: number;
  release: number;
}

const getEffectiveEnvelopeSettings = (): EnvelopeSettings => {
  if (!globalAdsrEnabled) {
    return {
      attack: ADSR_GATE_ATTACK,
      decay: ADSR_GATE_DECAY,
      sustain: ADSR_GATE_SUSTAIN,
      release: ADSR_GATE_RELEASE,
    };
  }

  return {
    attack: globalAttack,
    decay: globalDecay,
    sustain: globalSustain,
    release: globalRelease,
  };
};

const applyEnvelopeSettingsToNode = (
  envelope: Tone.AmplitudeEnvelope | null
): void => {
  if (!envelope) return;

  const settings = getEffectiveEnvelopeSettings();

  try {
    envelope.attack = settings.attack;
  } catch {}
  try {
    envelope.decay = settings.decay;
  } catch {}
  try {
    envelope.sustain = settings.sustain;
  } catch {}
  try {
    envelope.release = settings.release;
  } catch {}
};

const applyEnvelopeSettingsToStepVoices = (): void => {
  if (!stepVoices) return;

  stepVoices.forEach((voice) => {
    applyEnvelopeSettingsToNode(voice.envelope);
  });
};

const applyEnvelopeSettingsToAllEnvelopes = (): void => {
  applyEnvelopeSettingsToNode(globalEnvelope);
  applyEnvelopeSettingsToStepVoices();
};

const applySamplerReleaseMode = (sampler: Tone.Sampler | null): void => {
  if (!sampler) return;

  try {
    sampler.release = globalAdsrEnabled
      ? SAMPLER_DEFAULT_RELEASE
      : SAMPLER_CUT_RELEASE;
  } catch {}
};

const applySamplerReleaseModeToStepSamplers = (): void => {
  applySamplerReleaseMode(sharedStepSampler);
  applySamplerReleaseMode(reverseStepSampler);
};

// Expression parameters for FX
export interface ExpressionParams {
  attack: number; // Attack time in seconds
  release: number; // Release time in seconds
  maxValue: number; // Maximum value (0-1)
}

// ===== Per-step voice engine (16 independent sampler + FX chains) =====

export interface MasterFXSnapshot {
  filterCutoffHz: number; // 20 - 10000
  delayTime: string; // Tone.js time notation (e.g., "4n", "8n")
  delayFeedback: number; // 0-1
  delayMix: number; // 0-1
  reverbDecay: number; // seconds
  reverbMix: number; // 0-1
}

export interface StepVoiceChain {
  chainInput: Tone.Gain;
  regularGain: Tone.Gain;
  reverseGain: Tone.Gain;
  filter: Tone.Filter;
  envelope: Tone.AmplitudeEnvelope;
  dryGain: Tone.Gain;
  delaySend: Tone.Gain;
  reverbSend: Tone.Gain;
  output: Tone.Gain; // for possible per-voice volume
}

let stepVoices: StepVoiceChain[] | null = null;
// Keep original sample mapping for initial build; we don't auto-update voices on instrument change

let sharedStepSampler: Tone.Sampler | null = null;
let sharedSamplerSamplesMap: { [note: string]: string } | null = null;
const connectedStepVoices = new Set<number>();
let reverseStepSampler: Tone.Sampler | null = null;
let reverseSamplerSamplesMap: { [note: string]: string } | null = null;
const reverseBuffersMap = new Map<string, AudioBuffer>();
const connectedReverseStepVoices = new Set<number>();
let stepSharedDelay: Tone.FeedbackDelay | null = null;
let stepSharedReverb: Tone.Reverb | null = null;
let stepSharedOutput: Tone.Gain | null = null;

const normalizeSampleUrl = (url: string): string => {
  // "#" in file names is interpreted as a URL fragment in browsers.
  // Encode it so fetch/decode APIs request the actual file path.
  return url.startsWith("blob:") ? url : url.replace(/#/g, "%23");
};

const cloneSamplesMap = (samplesMap: {
  [note: string]: string;
}): { [note: string]: string } => {
  return Object.keys(samplesMap).reduce<{ [note: string]: string }>(
    (acc, key) => {
      acc[key] = normalizeSampleUrl(samplesMap[key]);
      return acc;
    },
    {}
  );
};

const disposeSharedStepSampler = (): void => {
  if (sharedStepSampler) {
    try {
      sharedStepSampler.disconnect();
    } catch {}
    try {
      sharedStepSampler.dispose();
    } catch {}
  }
  sharedStepSampler = null;
  sharedSamplerSamplesMap = null;
  connectedStepVoices.clear();
  disposeReverseStepSampler();
};

export const disposeReverseStepSampler = (): void => {
  if (reverseStepSampler) {
    try {
      reverseStepSampler.disconnect();
    } catch {}
    try {
      reverseStepSampler.dispose();
    } catch {}
  }
  reverseStepSampler = null;
  reverseSamplerSamplesMap = null;
  reverseBuffersMap.clear();
  connectedReverseStepVoices.clear();
};

const ensureStepSharedFx = (): void => {
  if (stepSharedDelay && stepSharedReverb && stepSharedOutput) {
    return;
  }

  stepSharedOutput = new Tone.Gain(1);

  stepSharedDelay = new Tone.FeedbackDelay({
    delayTime: globalDelayTime,
    feedback: globalDelayFeedback,
    wet: 1,
  });
  stepSharedDelay.connect(stepSharedOutput);

  stepSharedReverb = new Tone.Reverb({
    decay: globalReverbDecay,
    wet: 1,
    preDelay: 0.01,
  });
  stepSharedReverb.connect(stepSharedOutput);

  stepSharedOutput.toDestination();
};

const disposeStepSharedFx = (): void => {
  if (stepSharedDelay) {
    try {
      stepSharedDelay.dispose();
    } catch {}
  }
  if (stepSharedReverb) {
    try {
      stepSharedReverb.dispose();
    } catch {}
  }
  if (stepSharedOutput) {
    try {
      stepSharedOutput.dispose();
    } catch {}
  }
  stepSharedDelay = null;
  stepSharedReverb = null;
  stepSharedOutput = null;
};

const createSharedStepSampler = (samplesMap: {
  [note: string]: string;
}): Tone.Sampler => {
  disposeSharedStepSampler();
  sharedSamplerSamplesMap = cloneSamplesMap(samplesMap);
  sharedStepSampler = new Tone.Sampler({ urls: cloneSamplesMap(samplesMap) });
  applySamplerReleaseMode(sharedStepSampler);
  return sharedStepSampler;
};

export const createReverseStepSampler = async (samplesMap: {
  [note: string]: string;
}): Promise<Tone.Sampler | null> => {
  disposeReverseStepSampler();
  reverseSamplerSamplesMap = cloneSamplesMap(samplesMap);

  if (!sharedStepSampler) {
    createSharedStepSampler(samplesMap);
  }

  if (sharedStepSampler && !sharedStepSampler.loaded) {
    try {
      await Tone.loaded();
    } catch {}
  }

  const sourceSampler = sharedStepSampler;
  if (!sourceSampler) {
    return null;
  }

  reverseStepSampler = new Tone.Sampler({ urls: {} });
  applySamplerReleaseMode(reverseStepSampler);

  Object.keys(samplesMap).forEach((note) => {
    const sampleUrl = samplesMap[note];
    const bufferInfo = getSamplerBufferForNote(sourceSampler, note);
    const sourceBuffer = bufferInfo?.buffer.get();
    if (!sourceBuffer) {
      return;
    }

    const cachedBuffer = reverseBuffersMap.get(sampleUrl);
    const reversedBuffer = cachedBuffer || reverseAudioBuffer(sourceBuffer);
    if (!cachedBuffer) {
      reverseBuffersMap.set(sampleUrl, reversedBuffer);
    }

    reverseStepSampler?.add(note as any, reversedBuffer);
  });

  return reverseStepSampler;
};

const ensureSharedSamplerForVoice = (
  voiceIndex: number
): Tone.Sampler | null => {
  if (!stepVoices || !stepVoices[voiceIndex]) {
    return null;
  }

  if (!sharedStepSampler) {
    if (!sharedSamplerSamplesMap) {
      console.warn("Shared step sampler not initialized");
      return null;
    }
    createSharedStepSampler(sharedSamplerSamplesMap);
  }

  return sharedStepSampler;
};

const connectSharedSamplerToVoice = (
  voiceIndex: number
): Tone.Sampler | null => {
  const sampler = ensureSharedSamplerForVoice(voiceIndex);
  if (!sampler || !stepVoices || !stepVoices[voiceIndex]) {
    return null;
  }

  // Connect sampler to the regular gain input
  if (!connectedStepVoices.has(voiceIndex)) {
    try {
      sampler.connect(stepVoices[voiceIndex].regularGain);
      connectedStepVoices.add(voiceIndex);
    } catch (error) {
      console.error("Error connecting sampler to step voice:", error);
    }
  }

  return sampler;
};

const ensureReverseSamplerForVoice = (
  voiceIndex: number
): Tone.Sampler | null => {
  if (!stepVoices || !stepVoices[voiceIndex]) {
    return null;
  }

  if (!reverseStepSampler || !reverseSamplerSamplesMap) {
    return null;
  }

  return reverseStepSampler;
};

const connectReverseSamplerToVoice = (
  voiceIndex: number
): Tone.Sampler | null => {
  const sampler = ensureReverseSamplerForVoice(voiceIndex);
  if (!sampler || !stepVoices || !stepVoices[voiceIndex]) {
    return null;
  }

  if (!connectedReverseStepVoices.has(voiceIndex)) {
    try {
      sampler.connect(stepVoices[voiceIndex].reverseGain);
      connectedReverseStepVoices.add(voiceIndex);
    } catch (error) {
      console.error("Error connecting reverse sampler to step voice:", error);
    }
  }

  return sampler;
};

const getSamplerBufferForNote = (
  sampler: Tone.Sampler,
  note: string
): {
  buffer: any;
  playbackRate: number;
} | null => {
  const buffers = (sampler as any)._buffers;
  if (!buffers || typeof buffers.has !== "function") {
    return null;
  }

  const midiFloat = Tone.Frequency(note).toMidi();
  const midi = Math.round(midiFloat);
  const remainder = midiFloat - midi;
  const maxInterval = 96;
  let closestMidi: number | null = null;
  let difference = 0;

  for (let interval = 0; interval <= maxInterval; interval++) {
    if (buffers.has(midi + interval)) {
      closestMidi = midi + interval;
      difference = -interval;
      break;
    }
    if (buffers.has(midi - interval)) {
      closestMidi = midi - interval;
      difference = interval;
      break;
    }
  }

  if (closestMidi === null) {
    return null;
  }

  const buffer = buffers.get(closestMidi);
  const playbackRate = Math.pow(2, (difference + remainder) / 12);
  return { buffer, playbackRate };
};

export const reverseAudioBuffer = (buffer: AudioBuffer): AudioBuffer => {
  const reversedBuffer = Tone.context.createBuffer(
    buffer.numberOfChannels,
    buffer.length,
    buffer.sampleRate
  );

  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const sourceData = buffer.getChannelData(channel);
    const targetData = reversedBuffer.getChannelData(channel);
    for (let i = 0; i < buffer.length; i++) {
      targetData[i] = sourceData[buffer.length - 1 - i];
    }
  }

  return reversedBuffer;
};

export const getSampleDuration = async (
  sampler: Tone.Sampler,
  note: string
): Promise<number> => {
  const resolveDuration = (): number | null => {
    const info = getSamplerBufferForNote(sampler, note);
    if (!info) {
      return null;
    }
    const bufferDuration = info.buffer.duration;
    if (!bufferDuration || bufferDuration <= 0) {
      return null;
    }
    return bufferDuration / info.playbackRate;
  };

  const immediate = resolveDuration();
  if (immediate !== null) {
    return immediate;
  }

  if (!sampler.loaded) {
    try {
      await Tone.loaded();
    } catch {}
  }

  const resolved = resolveDuration();
  return resolved !== null ? resolved : 0;
};

/**
 * Build step voices: each chain receives audio from the shared sampler and routes through
 * Filter -> Envelope -> Delay -> Reverb -> Output -> Destination.
 * The graph is constructed once; during playback we only update node parameters.
 * @param samplesMap - The samples mapping for the sampler
 * @param stepsCount - Number of steps to create voices for (default: 16, max: 64)
 */
export const buildStepVoices = (
  samplesMap: {
    [note: string]: string;
  },
  stepsCount: number = 16
): StepVoiceChain[] => {
  const STEPS = Math.min(Math.max(stepsCount, 16), 64); // Clamp between 16 and 64
  // Note: we intentionally do not use 'entries' to avoid linter warnings

  // Build if not exists or size mismatch
  if (!stepVoices || stepVoices.length !== STEPS) {
    ensureStepSharedFx();
    // Dispose existing if any
    if (stepVoices) {
      try {
        stepVoices.forEach((v) => {
          v.chainInput.dispose();
          v.regularGain.dispose();
          v.reverseGain.dispose();
          v.filter.dispose();
          v.envelope.dispose();
          v.dryGain.dispose();
          v.delaySend.dispose();
          v.reverbSend.dispose();
          v.output.dispose();
        });
      } catch {}
    }

    stepVoices = Array.from({ length: STEPS }, () => {
      const chainInput = new Tone.Gain(1);
      const regularGain = new Tone.Gain(1);
      const reverseGain = new Tone.Gain(0);
      const filter = new Tone.Filter({
        type: globalFilterType,
        frequency: globalFilterFreq,
        rolloff: -12,
      });
      const envelope = new Tone.AmplitudeEnvelope(getEffectiveEnvelopeSettings());

      const dryGain = new Tone.Gain(1);
      const delaySend = new Tone.Gain(0);
      const reverbSend = new Tone.Gain(0);
      const output = new Tone.Gain(1);

      // Connect chain (gate early to avoid processing inactive voices)
      regularGain.connect(chainInput);
      reverseGain.connect(chainInput);
      chainInput.connect(envelope);
      envelope.connect(filter);
      filter.connect(dryGain);
      dryGain.connect(output);
      filter.connect(delaySend);
      filter.connect(reverbSend);
      output.toDestination();
      if (stepSharedDelay) {
        delaySend.connect(stepSharedDelay);
      }
      if (stepSharedReverb) {
        reverbSend.connect(stepSharedReverb);
      }

      return {
        chainInput,
        regularGain,
        reverseGain,
        filter,
        envelope,
        dryGain,
        delaySend,
        reverbSend,
        output,
      };
    });

    createSharedStepSampler(samplesMap);
  }

  // Do not modify existing voices when instruments change; preserve original samples

  return stepVoices as StepVoiceChain[];
};

/** Dispose existing and rebuild step voices with a new samples mapping. */
export const rebuildStepVoices = (
  samplesMap: {
    [note: string]: string;
  },
  stepsCount: number = 16
): StepVoiceChain[] => {
  if (stepVoices) {
    try {
      stepVoices.forEach((v) => {
        v.chainInput.dispose();
        v.regularGain.dispose();
        v.reverseGain.dispose();
        v.filter.dispose();
        v.envelope.dispose();
        v.dryGain.dispose();
        v.delaySend.dispose();
        v.reverbSend.dispose();
        v.output.dispose();
      });
    } catch {}
  }
  stepVoices = null;
  disposeSharedStepSampler();
  disposeReverseStepSampler();
  disposeStepSharedFx();
  const result = buildStepVoices(samplesMap, stepsCount);
  return result;
};

/** Access a specific voice's sampler. */
export const getStepVoiceSampler = (
  voiceIndex: number
): Tone.Sampler | null => {
  if (!stepVoices || !stepVoices[voiceIndex]) return null;
  return connectSharedSamplerToVoice(voiceIndex);
};

export const getStepVoiceReverseSampler = (
  voiceIndex: number
): Tone.Sampler | null => {
  if (!stepVoices || !stepVoices[voiceIndex]) return null;
  return connectReverseSamplerToVoice(voiceIndex);
};

/** Trigger only the envelope for a given voice. */
export const triggerStepVoiceEnvelope = (
  voiceIndex: number,
  duration: Tone.Unit.Time,
  time: Tone.Unit.Time
): void => {
  if (!stepVoices || !stepVoices[voiceIndex]) return;
  try {
    stepVoices[voiceIndex].envelope.triggerAttackRelease(duration, time);
  } catch {}
};

/** Trigger envelope attack only; release is scheduled separately. */
export const triggerStepVoiceEnvelopeAttack = (
  voiceIndex: number,
  time: Tone.Unit.Time
): void => {
  if (!stepVoices || !stepVoices[voiceIndex]) return;
  try {
    stepVoices[voiceIndex].envelope.triggerAttack(time);
  } catch {}
};

/** Release a single envelope to avoid mass release scheduling. */
export const releaseStepVoiceEnvelope = (
  voiceIndex: number,
  time: Tone.Unit.Time
): void => {
  if (!stepVoices || !stepVoices[voiceIndex]) return;
  try {
    stepVoices[voiceIndex].envelope.triggerRelease(time);
  } catch {}
};

/** Cut active voices on shared step samplers while preserving downstream FX tails. */
export const cutStepSamplerSources = (time: Tone.Unit.Time): void => {
  try {
    sharedStepSampler?.releaseAll(time);
  } catch {}

  try {
    reverseStepSampler?.releaseAll(time);
  } catch {}
};

/** Release all envelopes except the active one to prevent multiple voices from sounding unintentionally. */
export const releaseAllStepEnvelopesExcept = (
  activeIndex: number,
  time: Tone.Unit.Time
): void => {
  if (!stepVoices) return;
  stepVoices.forEach((v, i) => {
    if (i !== activeIndex) {
      try {
        v.envelope.triggerRelease(time);
      } catch {}
    }
  });
};

export const MIN_FILTER_CUTOFF = 20;
export const MAX_FILTER_CUTOFF = 20000;
const FILTER_RANGE = MAX_FILTER_CUTOFF - MIN_FILTER_CUTOFF;
const FILTER_CUTOFF_SMOOTH_TIME = 0.005;
const STEP_MIX_RAMP_TIME_SECONDS = 0.003;
const STEP_FX_RAMP_TIME_SECONDS = 0.004;

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

const applyFilterCutoff = (
  filter: Tone.Filter,
  frequency: number,
  options: { smooth?: boolean; rampTime?: number } = {}
): void => {
  const { smooth = true, rampTime = FILTER_CUTOFF_SMOOTH_TIME } = options;
  const safeFrequency = clamp(frequency, MIN_FILTER_CUTOFF, MAX_FILTER_CUTOFF);
  const now = Tone.now();

  try {
    filter.frequency.cancelScheduledValues(now);

    if (smooth) {
      filter.frequency.rampTo(safeFrequency, rampTime);
    } else {
      filter.frequency.setValueAtTime(safeFrequency, now);
    }
  } catch (err) {
    try {
      filter.frequency.setValueAtTime(safeFrequency, now);
    } catch {}
  }
};

/** Update a voice's FX parameters from a master snapshot (no node reconnections). */
export const applySnapshotToVoice = (
  voiceIndex: number,
  snapshot: MasterFXSnapshot,
  time?: Tone.Unit.Time
): void => {
  if (!stepVoices || !stepVoices[voiceIndex]) return;
  const v = stepVoices[voiceIndex];
  const scheduledTime =
    time !== undefined ? Tone.Time(time).toSeconds() : Tone.now();

  // Filter - set frequency directly on the filter node
  try {
    const safeFilterCutoff = clamp(
      snapshot.filterCutoffHz,
      MIN_FILTER_CUTOFF,
      MAX_FILTER_CUTOFF
    );

    v.filter.frequency.cancelScheduledValues(scheduledTime);
    v.filter.frequency.setValueAtTime(v.filter.frequency.value, scheduledTime);
    v.filter.frequency.linearRampToValueAtTime(
      safeFilterCutoff,
      scheduledTime + STEP_FX_RAMP_TIME_SECONDS
    );
  } catch (err) {
    console.error(`Error setting step ${voiceIndex} filter frequency:`, err);
  }

  // Delay (send-only; shared delay params updated via setters)
  try {
    const delayMix = clamp(snapshot.delayMix, 0, 1);
    v.delaySend.gain.cancelScheduledValues(scheduledTime);
    v.delaySend.gain.setValueAtTime(v.delaySend.gain.value, scheduledTime);
    v.delaySend.gain.linearRampToValueAtTime(
      delayMix,
      scheduledTime + STEP_FX_RAMP_TIME_SECONDS
    );
  } catch {
    v.delaySend.gain.value = clamp(snapshot.delayMix, 0, 1);
  }

  // Main Reverb
  try {
    const reverbMix = clamp(snapshot.reverbMix, 0, 1);
    v.reverbSend.gain.cancelScheduledValues(scheduledTime);
    v.reverbSend.gain.setValueAtTime(v.reverbSend.gain.value, scheduledTime);
    v.reverbSend.gain.linearRampToValueAtTime(
      reverbMix,
      scheduledTime + STEP_FX_RAMP_TIME_SECONDS
    );
  } catch {
    v.reverbSend.gain.value = clamp(snapshot.reverbMix, 0, 1);
  }
};

export const applyStepVoiceMix = (
  voiceIndex: number,
  regularMix: number,
  reverseMix: number,
  time?: Tone.Unit.Time
): void => {
  if (!stepVoices || !stepVoices[voiceIndex]) return;
  const v = stepVoices[voiceIndex];
  const regularValue = clamp(regularMix, 0, 1);
  const reverseValue = clamp(reverseMix, 0, 1);

  if (time !== undefined) {
    const timeSeconds = Tone.Time(time).toSeconds();
    try {
      v.regularGain.gain.cancelScheduledValues(timeSeconds);
      v.reverseGain.gain.cancelScheduledValues(timeSeconds);
      v.regularGain.gain.setValueAtTime(v.regularGain.gain.value, timeSeconds);
      v.reverseGain.gain.setValueAtTime(v.reverseGain.gain.value, timeSeconds);
      v.regularGain.gain.linearRampToValueAtTime(
        regularValue,
        timeSeconds + STEP_MIX_RAMP_TIME_SECONDS
      );
      v.reverseGain.gain.linearRampToValueAtTime(
        reverseValue,
        timeSeconds + STEP_MIX_RAMP_TIME_SECONDS
      );
    } catch {
      v.regularGain.gain.value = regularValue;
      v.reverseGain.gain.value = reverseValue;
    }
  } else {
    const now = Tone.now();
    try {
      v.regularGain.gain.cancelScheduledValues(now);
      v.reverseGain.gain.cancelScheduledValues(now);
      v.regularGain.gain.setValueAtTime(v.regularGain.gain.value, now);
      v.reverseGain.gain.setValueAtTime(v.reverseGain.gain.value, now);
      v.regularGain.gain.linearRampToValueAtTime(
        regularValue,
        now + STEP_MIX_RAMP_TIME_SECONDS
      );
      v.reverseGain.gain.linearRampToValueAtTime(
        reverseValue,
        now + STEP_MIX_RAMP_TIME_SECONDS
      );
    } catch {
      v.regularGain.gain.value = regularValue;
      v.reverseGain.gain.value = reverseValue;
    }
  }
};

// Default expression parameters for each FX
let filterExpression: ExpressionParams = {
  attack: 0.1,
  release: 0.2,
  maxValue: 0.0, // Set default to 0 (20Hz)
};

let reverbExpression: ExpressionParams = {
  attack: 0.1,
  release: 0.2,
  maxValue: 0.8,
};

let delayExpression: ExpressionParams = {
  attack: 0.1,
  release: 0.2,
  maxValue: 0.8,
};

// Track current expression states
interface ExpressionState {
  isActive: boolean;
  baseValue: number; // The value before expression was triggered
  isTracking: boolean; // Whether we're actively tracking parameter changes
  updateCallback?: (value: number) => void; // Callback for UI updates
}

let filterExpressionState: ExpressionState = {
  isActive: false,
  baseValue: 0,
  isTracking: false,
};

let reverbExpressionState: ExpressionState = {
  isActive: false,
  baseValue: 0,
  isTracking: false,
};

let delayExpressionState: ExpressionState = {
  isActive: false,
  baseValue: 0,
  isTracking: false,
};

// Parameter tracking system
let parameterTrackingInterval: number | null = null;

/**
 * Starts tracking parameter changes and updating UI
 */
const startParameterTracking = (): void => {
  if (parameterTrackingInterval) return; // Already tracking

  parameterTrackingInterval = window.setInterval(() => {
    // Track filter parameter
    if (
      filterExpressionState.isTracking &&
      globalFilter &&
      filterExpressionState.updateCallback
    ) {
      const currentFreq = Number(globalFilter.frequency.value);
      // Convert from frequency to UI value (0-1) using logarithmic mapping
      if (currentFreq <= MIN_FILTER_CUTOFF) {
        filterExpressionState.updateCallback(0);
      } else if (currentFreq >= MAX_FILTER_CUTOFF) {
        filterExpressionState.updateCallback(1);
      } else {
        const currentMix =
          Math.log(currentFreq / MIN_FILTER_CUTOFF) /
          Math.log(MAX_FILTER_CUTOFF / MIN_FILTER_CUTOFF);
        filterExpressionState.updateCallback(currentMix);
      }
    }

    // Track reverb parameter
    if (
      reverbExpressionState.isTracking &&
      globalReverb &&
      reverbExpressionState.updateCallback
    ) {
      const currentMix = Number(globalReverb.wet.value);
      reverbExpressionState.updateCallback(currentMix);
    }

    // Track delay parameter
    if (
      delayExpressionState.isTracking &&
      globalDelay &&
      delayExpressionState.updateCallback
    ) {
      const currentMix = Number(globalDelay.wet.value);
      delayExpressionState.updateCallback(currentMix);
    }

    // Stop tracking if no expressions are active
    if (
      !filterExpressionState.isTracking &&
      !reverbExpressionState.isTracking &&
      !delayExpressionState.isTracking
    ) {
      stopParameterTracking();
    }
  }, 16); // ~60fps for smooth UI updates
};

/**
 * Stops parameter tracking
 */
const stopParameterTracking = (): void => {
  if (parameterTrackingInterval) {
    window.clearInterval(parameterTrackingInterval);
    parameterTrackingInterval = null;
  }
};

/**
 * Explicitly disable tracking for a specific effect
 * Used when user is directly controlling a parameter
 */
export const disableExpressionTracking = (
  effectType: "filter" | "reverb" | "delay"
): void => {
  switch (effectType) {
    case "filter":
      filterExpressionState.isTracking = false;
      filterExpressionState.isActive = false;
      break;
    case "reverb":
      reverbExpressionState.isTracking = false;
      reverbExpressionState.isActive = false;
      break;
    case "delay":
      delayExpressionState.isTracking = false;
      delayExpressionState.isActive = false;
      break;
  }
};

let globalFilterFreq = MAX_FILTER_CUTOFF; // Default filter cutoff in Hz
let globalFilterType: BiquadFilterType = "lowpass"; // Default filter type
let globalReverbMix = 0.2; // Default reverb mix (0-1)
let globalReverbDecay = 1.5; // Default reverb decay time in seconds
let globalDelayTime: string = "4n"; // Default delay time (quarter note)
let globalDelayFeedback = 0.3; // Default delay feedback amount (0-1)
let globalDelayMix = 0.2; // Default delay mix (0-1)
let globalReverseMix = 0; // Default reverse mix (0-1)

// Reserved: list of active envelopes (currently not used)
// let activeEnvelopes: Tone.AmplitudeEnvelope[] = [];

// Create a global chain for processing
const createGlobalChain = (): Tone.AmplitudeEnvelope => {
  if (!globalEnvelope) {
    globalEnvelope = new Tone.AmplitudeEnvelope(getEffectiveEnvelopeSettings());
  }

  if (!globalFilter) {
    globalFilter = new Tone.Filter({
      type: globalFilterType,
      frequency: clamp(globalFilterFreq, MIN_FILTER_CUTOFF, MAX_FILTER_CUTOFF),
      rolloff: -12,
    });
    globalEnvelope.connect(globalFilter);
  }

  if (!globalDelay) {
    globalDelay = new Tone.FeedbackDelay({
      delayTime: globalDelayTime,
      feedback: globalDelayFeedback,
      wet: globalDelayMix,
    });
    globalFilter?.connect(globalDelay);
  }

  if (!globalReverb) {
    globalReverb = new Tone.Reverb({
      decay: globalReverbDecay,
      wet: globalReverbMix,
      preDelay: 0.01,
    });
    globalDelay?.connect(globalReverb);
    globalReverb.toDestination();
  }

  return globalEnvelope;
};

const ensureGlobalChainExists = (): void => {
  if (!globalEnvelope || !globalFilter || !globalDelay || !globalReverb) {
    createGlobalChain();
  }
};

/**
 * Creates a new Tone.js player with the given sample URL
 * @param sampleUrl URL of the audio sample to load
 * @param onLoad Callback function when sample is loaded
 * @param onError Callback function when sample fails to load
 * @returns Tone.Player instance
 */
export const createSamplePlayer = (
  sampleUrl: string,
  onLoad?: () => void,
  onError?: (error: Error) => void
): Tone.Player => {
  // Ensure the global chain is created
  const inputNode = createGlobalChain();

  const normalizedSampleUrl = normalizeSampleUrl(sampleUrl);
  console.log(`Creating sample player for: ${normalizedSampleUrl}`);

  // Create a player that will connect to the envelope
  const player = new Tone.Player({
    url: normalizedSampleUrl,
    onload: () => {
      console.log(`Sample loaded successfully: ${normalizedSampleUrl}`);
      console.log(`Player connected to global chain:`, !!inputNode);
      debugGlobalChain();
      if (onLoad) onLoad();
    },
    onerror: (error) => {
      console.error(`Error loading sample ${normalizedSampleUrl}:`, error);
      console.error("Error details:", {
        message: error.message,
        name: error.name,
        stack: error.stack,
      });
      if (onError) onError(error);
    },
  }).connect(inputNode);

  return player;
};

/**
 * Validates that an audio URL is accessible and returns a valid response
 * @param url The audio file URL to validate
 * @returns Promise that resolves if the URL is valid
 */
export const validateAudioUrl = async (url: string): Promise<void> => {
  // Skip validation for blob URLs since they're always local and can't be checked with HEAD
  if (url.startsWith("blob:")) {
    console.log("Blob URL detected, skipping validation");
    return;
  }

  try {
    const normalizedUrl = normalizeSampleUrl(url);
    const response = await fetch(normalizedUrl, { method: "HEAD" });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentType = response.headers.get("content-type");
    if (contentType && !contentType.startsWith("audio/")) {
      console.warn(`Warning: Content-Type is ${contentType}, expected audio/*`);
    }

    console.log(`Audio URL validated: ${normalizedUrl} (${contentType})`);
  } catch (error) {
    console.error(`Failed to validate audio URL ${url}:`, error);
    throw error;
  }
};

/**
 * Initializes the Tone.js audio context
 * This must be called in response to a user interaction (click, touch, etc.)
 * @returns Promise that resolves when Tone.js is started
 */
export const initializeToneContext = async (): Promise<void> => {
  try {
    // Check if audio context is already running
    if (Tone.context.state === "running") {
      console.log("Tone.js audio context already running");
      return;
    }

    await Tone.start();
    console.log("Tone.js audio context started successfully");
    console.log("Audio context state:", Tone.context.state);
    console.log("Audio context sample rate:", Tone.context.sampleRate);

    // Initialize the effects chain
    createGlobalChain();
  } catch (error) {
    console.error("Failed to initialize Tone.js audio context:", error);
    throw error;
  }
};

/**
 * Changes the playback rate (pitch) of a player
 * @param player Tone.Player instance
 * @param pitchOffset Number of semitones to transpose (-12 to +12)
 */
export const setPitch = (player: Tone.Player, pitchOffset: number): void => {
  // Convert semitones to playback rate
  // Formula: 2^(semitones/12)
  const rate = Math.pow(2, pitchOffset / 12);
  player.playbackRate = rate;
};

// ADSR Envelope Parameter Getters and Setters

/**
 * Sets the global attack time for all samples
 * @param attackTime Attack time in seconds
 */
export const setAttack = (attackTime: number): void => {
  globalAttack = attackTime;
  applyEnvelopeSettingsToAllEnvelopes();
  console.log(`Attack set to ${attackTime}s`);
};

/**
 * Gets the current global attack time
 * @returns Current attack time in seconds
 */
export const getAttack = (): number => {
  return globalAttack;
};

/**
 * Sets the global decay time for all samples
 * @param decayTime Decay time in seconds
 */
export const setDecay = (decayTime: number): void => {
  globalDecay = decayTime;
  applyEnvelopeSettingsToAllEnvelopes();
  console.log(`Decay set to ${decayTime}s`);
};

/**
 * Gets the current global decay time
 * @returns Current decay time in seconds
 */
export const getDecay = (): number => {
  return globalDecay;
};

/**
 * Sets the global sustain level for all samples
 * @param sustainLevel Sustain level from 0 to 1
 */
export const setSustain = (sustainLevel: number): void => {
  globalSustain = sustainLevel;
  applyEnvelopeSettingsToAllEnvelopes();
  console.log(`Sustain set to ${sustainLevel}`);
};

/**
 * Gets the current global sustain level
 * @returns Current sustain level (0-1)
 */
export const getSustain = (): number => {
  return globalSustain;
};

/**
 * Sets the global release time for all samples
 * @param releaseTime Release time in seconds
 */
export const setRelease = (releaseTime: number): void => {
  globalRelease = releaseTime;
  applyEnvelopeSettingsToAllEnvelopes();
  console.log(`Release set to ${releaseTime}s`);
};

export const setAdsrEnabled = (enabled: boolean): void => {
  globalAdsrEnabled = enabled;
  applyEnvelopeSettingsToAllEnvelopes();
  applySamplerReleaseModeToStepSamplers();
  console.log(`ADSR envelope ${enabled ? "enabled" : "disabled (gate mode)"}`);
};

export const getAdsrEnabled = (): boolean => {
  return globalAdsrEnabled;
};

/**
 * Gets the current global release time
 * @returns Current release time in seconds
 */
export const getRelease = (): number => {
  return globalRelease;
};

/**
 * Sets the global filter cutoff frequency
 * @param frequency Cutoff frequency in Hz
 */
export const setFilterCutoff = (frequency: number): void => {
  // Ensure the frequency is never below a safe minimum
  const safeFrequency = clamp(frequency, MIN_FILTER_CUTOFF, MAX_FILTER_CUTOFF);

  // Update our global tracked value
  globalFilterFreq = safeFrequency;

  ensureGlobalChainExists();

  // Update the actual filter node
  if (globalFilter) {
    try {
      applyFilterCutoff(globalFilter, safeFrequency);
      console.log(`Filter cutoff set to ${safeFrequency}Hz`);
    } catch (err) {
      console.error("Error setting filter cutoff:", err);
      // Fallback to direct assignment
      try {
        globalFilter.frequency.value = safeFrequency;
      } catch {}
    }
  }

  // Update all active step voice filters so currently sounding steps follow the global value
  if (stepVoices) {
    stepVoices.forEach((voice) => {
      try {
        applyFilterCutoff(voice.filter, safeFrequency);
      } catch {}
    });
  }
};

/**
 * Gets the current global filter cutoff frequency
 * @returns Current filter cutoff frequency in Hz
 */
export const getFilterCutoff = (): number => {
  return globalFilterFreq;
};

/**
 * Gets the current global filter type
 * @returns Current filter type
 */
export const getFilterType = (): BiquadFilterType => {
  return globalFilterType;
};

/**
 * Sets the global filter type
 * @param type Filter type (lowpass, highpass, bandpass, etc.)
 */
export const setFilterType = (type: BiquadFilterType): void => {
  globalFilterType = type;

  // Update the global filter if it exists
  if (globalFilter) {
    try {
      globalFilter.type = type;
      console.log(`Filter type set to ${type}`);
    } catch (err) {
      console.error("Error setting filter type:", err);
    }
  }

  // Update all step voices filters if they exist
  if (stepVoices) {
    stepVoices.forEach((voice) => {
      try {
        voice.filter.type = type;
      } catch (err) {
        console.error("Error setting step voice filter type:", err);
      }
    });
  }
};

/**
 * Sets the global reverb mix amount
 * @param mix Mix amount from 0 (dry) to 1 (wet)
 */
export const setReverbMix = (mix: number): void => {
  globalReverbMix = mix;
  if (globalReverb) {
    globalReverb.wet.value = mix;
  }
};

/**
 * Gets the current global reverb mix amount
 * @returns Current reverb mix amount (0-1)
 */
export const getReverbMix = (): number => {
  return globalReverbMix;
};

/**
 * Sets the global reverb decay time
 * @param decay Decay time in seconds
 */
export const setReverbDecay = (decay: number): void => {
  globalReverbDecay = decay;
  if (globalReverb && globalDelay) {
    // Since reverb decay can't be changed directly after creation,
    // we need to create a new reverb instance and reconnect it
    const oldReverb = globalReverb;
    const oldWet = oldReverb.wet.value;

    console.log(`Updating reverb decay to ${decay}s`);

    // Create new reverb with updated decay time
    const newReverb = new Tone.Reverb({
      decay,
      wet: oldWet,
      preDelay: 0.01,
    });

    // Wait for reverb to initialize
    newReverb
      .generate()
      .then(() => {
        try {
          // Reconnect to the chain (check globalDelay again since it's async)
          if (globalDelay) {
            globalDelay.disconnect(oldReverb);
            globalDelay.connect(newReverb);
            oldReverb.disconnect();
            newReverb.toDestination();
            globalReverb = newReverb;
            console.log(`Reverb decay updated successfully to ${decay}s`);
          } else {
            console.error("Global delay is null during reverb reconnection");
          }
        } catch (error) {
          console.error("Error updating reverb decay:", error);
          // If reconnection fails, try to restore the old reverb
          try {
            if (globalDelay) {
              globalDelay.connect(oldReverb);
              oldReverb.toDestination();
              console.log("Restored old reverb connection");
            }
          } catch (restoreError) {
            console.error("Failed to restore old reverb:", restoreError);
          }
        }
      })
      .catch((error) => {
        console.error("Error generating new reverb:", error);
      });
  }
  if (stepSharedReverb) {
    try {
      // @ts-ignore best-effort set
      if (typeof stepSharedReverb.set === "function") {
        stepSharedReverb.set({ decay });
      }
    } catch {}
  }
};

/**
 * Gets the current global reverb decay time
 * @returns Current reverb decay time in seconds
 */
export const getReverbDecay = (): number => {
  return globalReverbDecay;
};

/**
 * Sets the global delay time
 * @param time Delay time as Tone.js notation (e.g., "4n", "8n", "16n")
 */
export const setDelayTime = (time: string): void => {
  globalDelayTime = time;
  if (globalDelay) {
    globalDelay.delayTime.value = time;
  }
  if (stepSharedDelay) {
    stepSharedDelay.delayTime.value = time;
  }
};

/**
 * Gets the current global delay time
 * @returns Current delay time as Tone.js notation
 */
export const getDelayTime = (): string => {
  return globalDelayTime;
};

/**
 * Sets the global delay feedback amount
 * @param feedback Feedback amount from 0 to 1
 */
export const setDelayFeedback = (feedback: number): void => {
  globalDelayFeedback = feedback;
  if (globalDelay) {
    globalDelay.feedback.value = feedback;
  }
  if (stepSharedDelay) {
    stepSharedDelay.feedback.value = feedback;
  }
};

/**
 * Gets the current global delay feedback amount
 * @returns Current delay feedback amount (0-1)
 */
export const getDelayFeedback = (): number => {
  return globalDelayFeedback;
};

/**
 * Sets the global delay mix amount
 * @param mix Mix amount from 0 (dry) to 1 (wet)
 */
export const setDelayMix = (mix: number): void => {
  globalDelayMix = mix;
  if (globalDelay) {
    globalDelay.wet.value = mix;
  }
};

/**
 * Gets the current global delay mix amount
 * @returns Current delay mix amount (0-1)
 */
export const getDelayMix = (): number => {
  return globalDelayMix;
};

export const setReverseMix = (mix: number): void => {
  globalReverseMix = clamp(mix, 0, 1);
};

export const getReverseMix = (): number => {
  return globalReverseMix;
};

// Realtime getters that reflect current node parameters (including expression ramps)
export const getCurrentFilterCutoffHz = (): number => {
  try {
    ensureGlobalChainExists();

    if (!globalFilter) {
      return globalFilterFreq;
    }

    const param = globalFilter.frequency as unknown as {
      getValueAtTime?: (time: number) => number;
      value?: number;
    };

    let value: number | undefined;

    if (typeof param.getValueAtTime === "function") {
      value = param.getValueAtTime(Tone.now());
    }

    if (value === undefined || Number.isNaN(value)) {
      value = param.value !== undefined ? Number(param.value) : undefined;
    }

    if (value === undefined || Number.isNaN(value)) {
      value = globalFilterFreq;
    }

    return clamp(value, MIN_FILTER_CUTOFF, MAX_FILTER_CUTOFF);
  } catch (err) {
    console.error("Error reading filter frequency:", err);
    return clamp(globalFilterFreq, MIN_FILTER_CUTOFF, MAX_FILTER_CUTOFF);
  }
};

export const getCurrentDelayTime = (): string => {
  try {
    if (globalDelay) {
      const value = globalDelay.delayTime.value;
      // If it's already a string (Tone.js notation), return it
      if (typeof value === "string") {
        return value;
      }
      // If it's a number (legacy), return the stored global value
      return globalDelayTime;
    }
    return globalDelayTime;
  } catch {
    return globalDelayTime;
  }
};

export const getCurrentDelayFeedback = (): number => {
  try {
    return globalDelay
      ? Number(globalDelay.feedback.value)
      : globalDelayFeedback;
  } catch {
    return globalDelayFeedback;
  }
};

export const getCurrentDelayMix = (): number => {
  try {
    return globalDelay ? Number(globalDelay.wet.value) : globalDelayMix;
  } catch {
    return globalDelayMix;
  }
};

export const getCurrentReverbMix = (): number => {
  try {
    return globalReverb ? Number(globalReverb.wet.value) : globalReverbMix;
  } catch {
    return globalReverbMix;
  }
};

/**
 * Plays a sample as a one-shot (plays the entire sample once)
 * @param instrumentId The ID of the instrument to play
 */
export const playInstrumentOneShot = async (
  instrumentId: string
): Promise<void> => {
  // Get instrument from sampleUtils
  const { getMultiKeyInstrumentById } = await import("./sampleUtils");
  const instrument = getMultiKeyInstrumentById(instrumentId);

  if (!instrument) {
    console.warn(`No instrument found with ID: ${instrumentId}`);
    return;
  }

  // Get first available sample from the instrument
  const sampleEntries = Object.entries(instrument.samples);
  if (sampleEntries.length === 0) {
    console.warn(`No samples found for instrument: ${instrument.name}`);
    return;
  }

  // Use the first sample (usually C3)
  const [, samplePath] = sampleEntries[0];
  const normalizedSamplePath = normalizeSampleUrl(samplePath);

  // Create a temporary player for this one-shot
  const player = new Tone.Player({
    url: normalizedSamplePath,
    autostart: false,
    onload: () => {
      // Connect to the main chain
      const inputNode = createGlobalChain();
      player.connect(inputNode);

      // Play the entire sample
      player.start();

      // Dispose the player when playback is complete
      player.onstop = () => {
        player.dispose();
      };
    },
    onerror: (err) => {
      console.error(`Error loading sample ${normalizedSamplePath}:`, err);
    },
  }).toDestination();
};

/**
 * Loads a user-provided audio file into the sampler
 * Maps the file to C5 and lets Tone.js handle pitch stretching for other notes
 * @param file The audio file selected by the user
 * @returns A promise that resolves with the loaded sample info or rejects with an error
 */
export const loadCustomAudioFile = async (
  file: File
): Promise<{
  buffer: AudioBuffer;
  url: string;
  detectedPitch?: {
    note: string;
    octave: number;
    frequency: number | null;
    fullNoteName: string;
  };
}> => {
  return new Promise((resolve, reject) => {
    // Use FileReader to load the file into memory
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        if (!event.target || !event.target.result) {
          throw new Error("Failed to read file");
        }

        const audioContext = Tone.getContext().rawContext;
        const audioData = event.target.result as ArrayBuffer;

        // Check if the file size is reasonable
        if (audioData.byteLength === 0) {
          throw new Error("Empty audio file");
        }

        console.log(`Decoding audio data, size: ${audioData.byteLength} bytes`);

        // Use native Web Audio API for decoding
        audioContext.decodeAudioData(
          audioData,
          (decodedData) => {
            console.log("Audio successfully decoded, creating buffer");

            try {
              // Now create a buffer directly from the audio data for playback
              const buffer = Tone.context.createBuffer(
                decodedData.numberOfChannels,
                decodedData.length,
                decodedData.sampleRate
              );

              // Copy the decoded data to our buffer
              for (
                let channel = 0;
                channel < decodedData.numberOfChannels;
                channel++
              ) {
                const nowBuffering = buffer.getChannelData(channel);
                const sourceBuffer = decodedData.getChannelData(channel);
                for (let i = 0; i < decodedData.length; i++) {
                  nowBuffering[i] = sourceBuffer[i];
                }
              }

              console.log("Audio buffer created successfully");

              // Detect the pitch of the sample
              console.log("Analyzing sample pitch...");
              const detectedPitch = detectPitch(decodedData);

              // For compatibility, we'll also create a blob URL, but it won't be our primary method
              // We'll use the buffer directly when possible
              const fileUrl = URL.createObjectURL(file);

              resolve({
                buffer: decodedData,
                url: fileUrl,
                detectedPitch,
              });
            } catch (bufferError) {
              console.error("Error creating buffer:", bufferError);
              reject(
                new Error(
                  `Failed to create audio buffer: ${
                    bufferError instanceof Error
                      ? bufferError.message
                      : "Unknown error"
                  }`
                )
              );
            }
          },
          (decodeError) => {
            console.error("Error decoding audio data:", decodeError);
            reject(
              new Error(
                `Failed to decode audio file: ${
                  decodeError?.message || "Unknown error"
                }`
              )
            );
          }
        );
      } catch (error: unknown) {
        // Error loading the file
        console.error("Error loading custom audio file:", error);
        reject(error);
      }
    };

    reader.onerror = () => {
      console.error("Error reading file");
      reject(new Error("Error reading file"));
    };

    // Read the file as ArrayBuffer (better for audio processing)
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Creates a custom instrument using the uploaded audio file
 * @param buffer The audio buffer of the loaded file
 * @param fileName The name of the file for display purposes
 * @returns An object with the custom instrument details
 */
export const createCustomInstrument = (
  buffer: AudioBuffer,
  fileName: string
) => {
  // Create a custom instrument ID and name
  const id = "custom_" + Date.now();
  const name = fileName.replace(/\.[^/.]+$/, "") || "Custom Sample";

  // Create a custom instrument
  const customInstrument: MultiKeyInstrument = {
    id,
    name,
    category: "custom",
    samples: {
      // Map to C5 as the base note
      c5: "custom_sample", // This is just a placeholder, we'll use the buffer directly
    },
  };

  return {
    instrument: customInstrument,
    buffer,
  };
};

// Removed since we're not using this function anymore

/**
 * Updates the current active instrument to use a custom audio file
 * @param file The audio file to use as the new instrument
 */

/**
 * Creates a custom sample player from a File object
 * This follows the same pattern as createSamplePlayer but works with File objects
 */
export const createCustomSamplePlayer = async (
  file: File,
  onLoad?: () => void,
  onError?: (error: Error) => void
): Promise<{
  player: Tone.Player;
  fileUrl: string;
}> => {
  return new Promise((resolve, reject) => {
    try {
      // Create a blob URL for the file
      const fileUrl = URL.createObjectURL(file);
      console.log(`Creating custom sample player with URL: ${fileUrl}`);

      // Ensure the global chain is created - this is critical for sequencer
      const inputNode = createGlobalChain();

      // Create a player that will connect to the global processing chain
      const player = new Tone.Player({
        url: fileUrl,
        onload: () => {
          console.log(`Custom sample loaded successfully: ${file.name}`);
          console.log(`Player connected to global chain:`, !!inputNode);
          debugGlobalChain();
          if (onLoad) onLoad();
          resolve({ player, fileUrl });
        },
        onerror: (error) => {
          console.error(`Error loading custom sample ${file.name}:`, error);
          console.error("Error details:", {
            message: error.message,
            name: error.name,
            stack: error.stack,
          });
          URL.revokeObjectURL(fileUrl);
          if (onError) onError(error);
          reject(error);
        },
      }).connect(inputNode);
    } catch (error) {
      console.error("Error creating custom sample player:", error);
      const typedError =
        error instanceof Error ? error : new Error(String(error));
      if (onError) onError(typedError);
      reject(typedError);
    }
  });
};

export const loadCustomSampleToInstrument = async (
  file: File
): Promise<{
  instrumentId: string;
  instrumentName: string;
  detectedNote?: string;
}> => {
  try {
    // First load and analyze the audio file
    const { url: fileUrl, detectedPitch } = await loadCustomAudioFile(file);

    // Create a custom player with the loaded buffer
    const { player } = await createCustomSamplePlayer(
      file,
      () => console.log("Custom sample loaded successfully in instrument"),
      (error) =>
        console.error("Error loading custom sample in instrument:", error)
    );

    // Base note for mapping (will use detected pitch if available)
    const baseNote = detectedPitch?.fullNoteName?.toLowerCase() || "c5";
    const prettyNote = detectedPitch?.fullNoteName || "C5";

    // Use the detected pitch in the name if available
    const fileName = file.name.replace(/\.[^/.]+$/, "");
    const instrumentName = detectedPitch?.frequency
      ? `${fileName} (${prettyNote})`
      : fileName || "Custom Sample";

    // Create a custom instrument definition
    const customInstrument = {
      id: "custom_" + Date.now(),
      name: instrumentName,
      category: "custom",
      // Use the detected pitch as the base note if available
      baseNote: baseNote,
      samples: {
        // Map to the detected note as the root note
        [baseNote]: fileUrl,
        // Also map to standard octaves for compatibility
        c2: fileUrl,
        c3: fileUrl,
        c4: fileUrl,
        c5: fileUrl, // Main sample at C5
      },
    };

    // Register the custom instrument
    const { registerCustomInstrument } = await import("./sampleUtils");
    await registerCustomInstrument(customInstrument, player);

    console.log(
      "Custom instrument registered successfully:",
      customInstrument.id
    );

    if (detectedPitch?.frequency) {
      console.log(
        `Detected note: ${prettyNote} (${detectedPitch.frequency.toFixed(
          2
        )} Hz)`
      );
    }

    return {
      instrumentId: customInstrument.id,
      instrumentName: customInstrument.name,
      detectedNote: detectedPitch?.fullNoteName,
    };
  } catch (error) {
    console.error("Failed to load custom sample:", error);
    throw error;
  }
};

/**
 * Removes disposed envelopes from the active envelopes list
 */
// Reserved utility placeholder (currently unused) to cleanup disposed envelopes

/**
 * Detects the pitch of an audio sample
 * @param audioBuffer The AudioBuffer containing the sample
 * @returns Object with detected note name, octave, and frequency
 */
export const detectPitch = (
  audioBuffer: AudioBuffer
): {
  note: string;
  octave: number;
  frequency: number | null;
  fullNoteName: string;
} => {
  try {
    // Create detector using YIN algorithm (good balance of accuracy and performance)
    const detectPitchFunc = PitchFinder.YIN({
      sampleRate: audioBuffer.sampleRate,
    });

    // Get the audio data from the first channel
    const signal = audioBuffer.getChannelData(0);

    if (signal.length === 0) {
      console.warn("Empty audio buffer provided to pitch detector");
      return { note: "C", octave: 5, frequency: null, fullNoteName: "C5" };
    }

    // Find the most prominent section of the sample
    // We'll use a small portion after the initial attack for better detection
    const sampleLength = signal.length;
    const startPosition = Math.floor(sampleLength * 0.1); // Skip the first 10% (often attack transients)
    const analyzeLength = Math.min(16384, sampleLength - startPosition); // Use at most 16k samples
    const signalSegment = signal.slice(
      startPosition,
      startPosition + analyzeLength
    );

    // Detect multiple pitches and take the most confident one
    const frequencies: number[] = [];
    const confidences: number[] = [];

    // Analyze smaller chunks to find the most stable pitch
    const chunkSize = 2048;
    const maxChunks = 8; // Analyze up to 8 chunks

    for (
      let i = 0;
      i < maxChunks && i * chunkSize < signalSegment.length;
      i++
    ) {
      const chunk = signalSegment.slice(i * chunkSize, (i + 1) * chunkSize);
      const frequency = detectPitchFunc(chunk);

      if (frequency && frequency > 20 && frequency < 5000) {
        // Filter out extreme frequencies
        frequencies.push(frequency);

        // Calculate a basic confidence based on signal energy in this chunk
        let energy = 0;
        for (let j = 0; j < chunk.length; j++) {
          energy += chunk[j] * chunk[j];
        }
        confidences.push(energy);
      }
    }

    if (frequencies.length === 0) {
      console.log("No valid pitch detected, using default C5");
      return { note: "C", octave: 5, frequency: null, fullNoteName: "C5" };
    }

    // Find the most confident frequency
    let bestFrequency = 0;
    let highestConfidence = 0;

    for (let i = 0; i < frequencies.length; i++) {
      if (confidences[i] > highestConfidence) {
        highestConfidence = confidences[i];
        bestFrequency = frequencies[i];
      }
    }

    // Convert frequency to note information
    const noteInfo = frequencyToNote(bestFrequency);
    console.log(
      `Detected pitch: ${noteInfo.fullNoteName} (${bestFrequency.toFixed(
        2
      )} Hz)`
    );

    return {
      ...noteInfo,
      frequency: bestFrequency,
    };
  } catch (error) {
    console.error("Error during pitch detection:", error);
    return { note: "C", octave: 5, frequency: null, fullNoteName: "C5" };
  }
};

/**
 * Converts a frequency to a musical note name and octave
 * @param frequency The frequency in Hz
 * @returns Object with note name, octave and full note name
 */
export const frequencyToNote = (
  frequency: number
): {
  note: string;
  octave: number;
  fullNoteName: string;
} => {
  // A4 = 440Hz
  const noteNames = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];

  // Calculate semitones away from A4 (440Hz)
  const semitoneFromA4 = 12 * Math.log2(frequency / 440);

  // Calculate octave (A4 is in octave 4)
  // We add 9 to adjust from A4 reference point to C reference point
  const octave = Math.floor((semitoneFromA4 + 9) / 12) + 4;

  // Get semitone within octave (0-11, where 0 is C)
  const semitonesFromC = Math.round(semitoneFromA4 + 9) % 12;

  // Ensure we get a positive index for the note name
  const noteIndex = (semitonesFromC + 12) % 12;
  const note = noteNames[noteIndex];

  return {
    note,
    octave,
    fullNoteName: `${note}${octave}`,
  };
};
// const cleanupActiveEnvelopes = () => {
//   activeEnvelopes = activeEnvelopes.filter((env) => {
//     try {
//       // If accessing a property throws an error, the envelope is disposed
//       const attack = env.attack; // read to assert
//       return !!attack || attack === 0;
//     } catch {
//       return false;
//     }
//   });
// };

/**
 * Enhanced sampler that automatically triggers the global envelope
 */
class EnvelopedSampler extends Tone.Sampler {
  private _isLoaded: boolean = false;

  constructor(options?: Partial<Tone.SamplerOptions>) {
    super({
      ...options,
      onload: () => {
        this._isLoaded = true;
        console.log("EnvelopedSampler loaded successfully");
        if (options?.onload) {
          options.onload();
        }
      },
      onerror: (error) => {
        this._isLoaded = false;
        console.error("EnvelopedSampler failed to load:", error);
        if (options?.onerror) {
          options.onerror(error);
        }
      },
    });
  }

  // Getter for loading state
  get isLoaded(): boolean {
    return this._isLoaded && this.loaded;
  }

  // Override triggerAttack to check loading state
  triggerAttack(
    notes: Tone.Unit.Frequency | Tone.Unit.Frequency[],
    time?: Tone.Unit.Time,
    velocity?: number
  ): this {
    if (!this.isLoaded) {
      console.warn("Cannot trigger attack: sampler not loaded");
      return this;
    }

    // Trigger the sampler's built-in envelope
    super.triggerAttack(notes, time, velocity);

    // Also trigger the global envelope
    if (globalEnvelope) {
      globalEnvelope.triggerAttack(time || Tone.now());
    }

    return this;
  }

  // Override triggerRelease to check loading state
  triggerRelease(
    notes: Tone.Unit.Frequency | Tone.Unit.Frequency[],
    time?: Tone.Unit.Time
  ): this {
    if (!this.isLoaded) {
      console.warn("Cannot trigger release: sampler not loaded");
      return this;
    }

    // Release the sampler's built-in envelope
    super.triggerRelease(notes, time);

    // Also release the global envelope
    if (globalEnvelope) {
      globalEnvelope.triggerRelease(time || Tone.now());
    }

    return this;
  }

  // Override triggerAttackRelease to check loading state
  triggerAttackRelease(
    notes: Tone.Unit.Frequency | Tone.Unit.Frequency[],
    duration: Tone.Unit.Time,
    time?: Tone.Unit.Time,
    velocity?: number
  ): this {
    if (!this.isLoaded) {
      console.warn("Cannot trigger attack/release: sampler not loaded");
      return this;
    }

    // Trigger the sampler's built-in envelope
    super.triggerAttackRelease(notes, duration, time, velocity);

    // Also trigger the global envelope with the same timing
    if (globalEnvelope) {
      const triggerTime = time || Tone.now();
      globalEnvelope.triggerAttackRelease(duration, triggerTime);
    }

    return this;
  }
}

/**
 * Creates a sampler with the global effects chain for better sound processing
 * @param samples Optional sample mapping object for the sampler
 * @param baseUrl Optional base URL for loading samples
 * @param onLoad Callback function when all samples are loaded
 * @param onError Callback function when sample loading fails
 * @returns EnvelopedSampler instance connected to the global effects chain
 */
export const createEnvelopedSampler = (
  samples?: { [note: string]: string },
  baseUrl?: string,
  onLoad?: () => void,
  onError?: (error: Error) => void
): EnvelopedSampler => {
  // Ensure the global chain is created
  const inputNode = createGlobalChain();

  console.log("Creating sampler with samples:", samples);
  const normalizedSamples = samples ? cloneSamplesMap(samples) : undefined;

  // Create a simple sampler following the official Tone.js example
  const sampler = new EnvelopedSampler({
    urls: normalizedSamples,
    onload: () => {
      console.log("Sampler loaded successfully");
      if (onLoad) onLoad();
    },
    onerror: (error) => {
      console.error("Error loading sampler:", error);
      if (onError) onError(error);
    },
  }).connect(inputNode);

  return sampler;
};

/**
 * Creates a basic sampler connected to the global effects chain
 * This is a simplified version for quick sampler creation
 * @returns EnvelopedSampler instance connected to the global effects chain
 */
export const createBasicSampler = (): EnvelopedSampler => {
  // Ensure the global chain is created
  const inputNode = createGlobalChain();

  // Create a basic sampler connected to the global effects chain using our enhanced class
  const sampler = new EnvelopedSampler().connect(inputNode);

  return sampler;
};

/**
 * Format transport time to display friendly format
 * @param seconds Time in seconds
 * @returns Formatted time string (MM:SS.MS)
 */
export const formatTransportTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);

  return `${minutes.toString().padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
};

/**
 * Checks if the global audio chain is properly connected
 * @returns Boolean indicating if the chain is connected
 */
export const isGlobalChainConnected = (): boolean => {
  try {
    return !!(globalEnvelope && globalFilter && globalDelay && globalReverb);
  } catch (error) {
    console.error("Error checking global chain:", error);
    return false;
  }
};

/**
 * Logs the current state of the global audio chain for debugging
 */
export const debugGlobalChain = (): void => {
  console.log("Global Audio Chain Debug:");
  console.log("- Envelope:", !!globalEnvelope);
  console.log("- Filter:", !!globalFilter);
  console.log("- Delay:", !!globalDelay);
  console.log("- Reverb:", !!globalReverb);
  console.log("- Tone context state:", Tone.context.state);
  console.log("- Transport state:", Tone.Transport.state);
};

// Output meter for monitoring levels and clipping
let outputMeter: Tone.Meter | null = null;
const OUTPUT_METER_CLIP_THRESHOLD = 0.98;
const OUTPUT_METER_SENSITIVITY = 3.0;

const ensureOutputMeter = (): Tone.Meter => {
  if (!outputMeter) {
    outputMeter = new Tone.Meter({
      normalRange: true,
      smoothing: 0.85,
      channelCount: 2,
    });
    try {
      Tone.getDestination().connect(outputMeter);
    } catch (error) {
      console.error("Failed to connect output meter:", error);
    }
  }
  return outputMeter;
};

export const getOutputMeterLevel = (): {
  levels: [number, number];
  isClipping: boolean;
} => {
  try {
    const meter = ensureOutputMeter();
    const value = meter.getValue();
    const rawLevels = Array.isArray(value) ? value : [value, value];
    const safeLevels = rawLevels.map((raw) =>
      Math.min(Math.max(Number.isFinite(raw) ? raw : 0, 0), 1)
    );
    const scaledLevels = safeLevels.map((level) =>
      Math.min(level * OUTPUT_METER_SENSITIVITY, 1)
    );
    const levelLeft = scaledLevels[0] ?? 0;
    const levelRight = scaledLevels[1] ?? levelLeft;
    const levels: [number, number] = [levelLeft, levelRight];
    return {
      levels,
      isClipping: levels.some((level) => level >= OUTPUT_METER_CLIP_THRESHOLD),
    };
  } catch (error) {
    console.error("Error reading output meter:", error);
    return { levels: [0, 0], isClipping: false };
  }
};

export const disposeOutputMeter = (): void => {
  if (!outputMeter) return;
  try {
    Tone.getDestination().disconnect(outputMeter);
  } catch {}
  try {
    outputMeter.dispose();
  } catch {}
  outputMeter = null;
};

// Add a new export for directly triggering the envelope
/**
 * Explicitly triggers the envelope with attack and release
 * @param time Time to trigger the envelope
 * @param duration Note duration before release (if not provided, will use attack + decay)
 */
export const triggerEnvelope = (
  time: number | string,
  duration?: number
): void => {
  if (!globalEnvelope) {
    console.warn("No envelope available to trigger");
    debugGlobalChain();
    return;
  }

  try {
    // Start the attack phase
    globalEnvelope.triggerAttack(time);

    // Calculate when to trigger release
    let releaseTime: number | string;

    if (duration !== undefined) {
      // If duration is provided, use it directly
      if (typeof time === "number" && typeof duration === "number") {
        releaseTime = time + duration;
      } else {
        releaseTime = `+${duration}`;
      }
    } else {
      // If no duration, use attack + decay as the hold time
      if (typeof time === "number") {
        releaseTime = time + globalAttack + globalDecay;
      } else {
        releaseTime = `+${globalAttack + globalDecay}`;
      }
    }

    // Schedule the release
    globalEnvelope.triggerRelease(releaseTime);

    console.log(
      `Envelope triggered with A:${globalAttack}, D:${globalDecay}, S:${globalSustain}, R:${globalRelease}`
    );
  } catch (error) {
    console.error("Error triggering envelope:", error);
    debugGlobalChain();
  }
};

// Expression Parameter Management Functions

/**
 * Gets the current filter expression parameters
 */
export const getFilterExpression = (): ExpressionParams => {
  return { ...filterExpression };
};

/**
 * Sets the filter expression parameters
 */
export const setFilterExpression = (params: ExpressionParams): void => {
  filterExpression = { ...params };
};

/**
 * Gets the current reverb expression parameters
 */
export const getReverbExpression = (): ExpressionParams => {
  return { ...reverbExpression };
};

/**
 * Sets the reverb expression parameters
 */
export const setReverbExpression = (params: ExpressionParams): void => {
  reverbExpression = { ...params };
};

/**
 * Gets the current delay expression parameters
 */
export const getDelayExpression = (): ExpressionParams => {
  return { ...delayExpression };
};

/**
 * Sets the delay expression parameters
 */
export const setDelayExpression = (params: ExpressionParams): void => {
  delayExpression = { ...params };
};

// Rate-based FX Control Functions

/**
 * Sets the UI update callback for filter expression
 */
export const setFilterExpressionUICallback = (
  callback: (value: number) => void
): void => {
  filterExpressionState.updateCallback = callback;
};

/**
 * Sets the UI update callback for reverb expression
 */
export const setReverbExpressionUICallback = (
  callback: (value: number) => void
): void => {
  reverbExpressionState.updateCallback = callback;
};

/**
 * Sets the UI update callback for delay expression
 */
export const setDelayExpressionUICallback = (
  callback: (value: number) => void
): void => {
  delayExpressionState.updateCallback = callback;
};

/**
 * Triggers filter expression - ramps to max value over attack time
 */
export const triggerFilterExpression = (): void => {
  if (!globalFilter || filterExpressionState.isActive) return;

  // Store the current base value (0-1 range)
  // Calculate UI value from frequency: 0 = lowest freq, 1 = highest freq
  filterExpressionState.baseValue = clamp(
    (Number(globalFilter.frequency.value) - MIN_FILTER_CUTOFF) / FILTER_RANGE,
    0,
    1
  );
  filterExpressionState.isActive = true;
  filterExpressionState.isTracking = true;

  // Start parameter tracking
  startParameterTracking();

  // Calculate target frequency based on maxValue (UI value 0-1)
  // For filter expression, we want minimum value (0) to mean lowest cutoff (20Hz)
  // and maximum value to mean highest cutoff (10000Hz)
  const targetFreq =
    filterExpression.maxValue > 0
      ? MIN_FILTER_CUTOFF + filterExpression.maxValue * FILTER_RANGE
      : MIN_FILTER_CUTOFF; // Ensure minimum value is exactly 20Hz

  // Ramp to target value over attack time
  globalFilter.frequency.rampTo(targetFreq, filterExpression.attack);

  console.log(
    `Filter expression triggered: ramping to ${
      filterExpression.maxValue * 100
    }% over ${filterExpression.attack}s`
  );
};

/**
 * Releases filter expression - ramps back to base value over release time
 */
export const releaseFilterExpression = (): void => {
  if (!globalFilter || !filterExpressionState.isActive) return;

  filterExpressionState.isActive = false;

  // Immediately stop tracking to prevent conflicts with manual control
  filterExpressionState.isTracking = false;

  // Ramp back to base value over release time
  // Convert from base UI value (0-1) back to frequency
  const targetFreq =
    MIN_FILTER_CUTOFF + filterExpressionState.baseValue * FILTER_RANGE;
  globalFilter.frequency.rampTo(targetFreq, filterExpression.release);

  // Final UI update to base value after ramp completes
  setTimeout(() => {
    if (filterExpressionState.updateCallback) {
      filterExpressionState.updateCallback(filterExpressionState.baseValue);
    }
  }, filterExpression.release * 1000);

  console.log(
    `Filter expression released: ramping back to base over ${filterExpression.release}s`
  );
};

/**
 * Triggers reverb expression - ramps to max value over attack time
 */
export const triggerReverbExpression = (): void => {
  if (!globalReverb || reverbExpressionState.isActive) return;

  // Store the current base value
  reverbExpressionState.baseValue = Number(globalReverb.wet.value);
  reverbExpressionState.isActive = true;
  reverbExpressionState.isTracking = true;

  // Start parameter tracking
  startParameterTracking();

  // Ramp to target value over attack time
  globalReverb.wet.rampTo(reverbExpression.maxValue, reverbExpression.attack);

  console.log(
    `Reverb expression triggered: ramping to ${
      reverbExpression.maxValue * 100
    }% over ${reverbExpression.attack}s`
  );
};

/**
 * Releases reverb expression - ramps back to base value over release time
 */
export const releaseReverbExpression = (): void => {
  if (!globalReverb || !reverbExpressionState.isActive) return;

  reverbExpressionState.isActive = false;

  // Ramp back to base value over release time
  globalReverb.wet.rampTo(
    reverbExpressionState.baseValue,
    reverbExpression.release
  );

  // Schedule stopping tracking after release time completes
  setTimeout(() => {
    reverbExpressionState.isTracking = false;
    // Final UI update to base value
    if (reverbExpressionState.updateCallback) {
      reverbExpressionState.updateCallback(reverbExpressionState.baseValue);
    }
  }, reverbExpression.release * 1000);

  console.log(
    `Reverb expression released: ramping back to base over ${reverbExpression.release}s`
  );
};

/**
 * Triggers delay expression - ramps to max value over attack time
 */
export const triggerDelayExpression = (): void => {
  if (!globalDelay || delayExpressionState.isActive) return;

  // Store the current base value
  delayExpressionState.baseValue = Number(globalDelay.wet.value);
  delayExpressionState.isActive = true;
  delayExpressionState.isTracking = true;

  // Start parameter tracking
  startParameterTracking();

  // Ramp to target value over attack time
  globalDelay.wet.rampTo(delayExpression.maxValue, delayExpression.attack);

  console.log(
    `Delay expression triggered: ramping to ${
      delayExpression.maxValue * 100
    }% over ${delayExpression.attack}s`
  );
};

/**
 * Releases delay expression - ramps back to base value over release time
 */
export const releaseDelayExpression = (): void => {
  if (!globalDelay || !delayExpressionState.isActive) return;

  delayExpressionState.isActive = false;

  // Ramp back to base value over release time
  globalDelay.wet.rampTo(
    delayExpressionState.baseValue,
    delayExpression.release
  );

  // Schedule stopping tracking after release time completes
  setTimeout(() => {
    delayExpressionState.isTracking = false;
    // Final UI update to base value
    if (delayExpressionState.updateCallback) {
      delayExpressionState.updateCallback(delayExpressionState.baseValue);
    }
  }, delayExpression.release * 1000);

  console.log(
    `Delay expression released: ramping back to base over ${delayExpression.release}s`
  );
};

/**
 * Gets the current expression state for all FX
 */

export const getExpressionStates = () => {
  return {
    filter: { ...filterExpressionState },
    reverb: { ...reverbExpressionState },
    delay: { ...delayExpressionState },
  };
};
